import React from 'react';
import { 
  Modal, 
  Row, 
  Col, 
  Typography, 
  Tag, 
  Badge, 
  Divider, 
  Space,
  Button,
  Tooltip,
  Spin
} from 'antd';
import { AlertOutlined, CopyOutlined, LinkOutlined } from '@ant-design/icons';
import { VulnerabilidadeCSA } from '../../../types/dashboard/csa.types';

const { Text, Paragraph, Link } = Typography;

interface CSAVulnerabilityModalProps {
  visible: boolean;
  vulnerability: VulnerabilidadeCSA | null;
  onClose: () => void;
  loading?: boolean;
}

const CSAVulnerabilityModal: React.FC<CSAVulnerabilityModalProps> = ({
  visible,
  vulnerability,
  onClose,
  loading = false
}) => {
  if (!vulnerability && !loading) return null;

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text).then(() => {
      console.log(`${label} copiado para clipboard`);
    });
  };

  // Função para extrair e converter texto em links clicáveis
  const renderTextWithLinks = (text: string, useCodeStyle: boolean = false) => {
    if (!text) return <Text>N/A</Text>;

    // Regex para detectar URLs no texto
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = text.split(urlRegex);

    return (
      <div style={{ whiteSpace: 'pre-wrap' }}>
        {parts.map((part, index) => {
          if (urlRegex.test(part)) {
            return (
              <Link
                key={index}
                href={part}
                target="_blank"
                rel="noopener noreferrer"
                style={{ 
                  color: '#1890ff',
                  textDecoration: 'underline',
                  wordBreak: 'break-all'
                }}
              >
                <LinkOutlined style={{ marginRight: 4 }} />
                {part}
              </Link>
            );
          }
          return useCodeStyle ? (
            <Text code key={index}>{part}</Text>
          ) : (
            <Text key={index}>{part}</Text>
          );
        })}
      </div>
    );
  };

  // Função específica para métricas CVSS (mantém estilo de código)
  const renderCVSSMetrics = (cvssMetrics: string) => {
    return renderTextWithLinks(cvssMetrics, true);
  };

  // Função específica para referências (estilo normal)
  const renderReferences = (references: string) => {
    return renderTextWithLinks(references, false);
  };

  return (
    <Modal
      title={
        <Space>
          <AlertOutlined />
          Detalhes da Vulnerabilidade
        </Space>
      }
      open={visible}
      onCancel={onClose}
      footer={[
        <Button key="close" onClick={onClose} disabled={loading}>
          Fechar
        </Button>
      ]}
      width={800}
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>
            <Text>Carregando detalhes da vulnerabilidade...</Text>
          </div>
        </div>
      ) : vulnerability ? (
        <div>
          {/* Informações básicas */}
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Text strong>CVE:</Text>
              <br />
              <Space>
                <Text code>{vulnerability.cve}</Text>
                <Tooltip title="Copiar CVE">
                  <Button 
                    type="text" 
                    size="small"
                    icon={<CopyOutlined />}
                    onClick={() => copyToClipboard(vulnerability.cve, 'CVE')}
                  />
                </Tooltip>
              </Space>
            </Col>
            <Col span={12}>
              <Text strong>Severidade:</Text>
              <br />
              <Tag color={vulnerability.severityInfo.color}>
                {vulnerability.severityInfo.label}
              </Tag>
            </Col>
            <Col span={12}>
              <Text strong>Host:</Text>
              <br />
              <Space>
                <Text>{vulnerability.host}</Text>
                <Tooltip title="Copiar Host">
                  <Button 
                    type="text" 
                    size="small"
                    icon={<CopyOutlined />}
                    onClick={() => copyToClipboard(vulnerability.host, 'Host')}
                  />
                </Tooltip>
              </Space>
            </Col>
            <Col span={12}>
              <Text strong>IP:</Text>
              <br />
              <Space>
                <Text code>{vulnerability.ip}</Text>
                <Tooltip title="Copiar IP">
                  <Button 
                    type="text" 
                    size="small"
                    icon={<CopyOutlined />}
                    onClick={() => copyToClipboard(vulnerability.ip, 'IP')}
                  />
                </Tooltip>
              </Space>
            </Col>
            <Col span={12}>
              <Text strong>Porta:</Text>
              <br />
              <Text>{vulnerability.port}</Text>
            </Col>
            <Col span={12}>
              <Text strong>Score CVSS:</Text>
              <br />
              <Space>
                <Badge
                  count={vulnerability.cvssScore}
                  style={{ backgroundColor: vulnerability.riscoGeral.cor }}
                  showZero
                />
                <Text type="secondary">({vulnerability.riscoGeral.nivel})</Text>
              </Space>
            </Col>
          </Row>

          <Divider />

          {/* Resumo */}
          <div style={{ marginBottom: 16 }}>
            <Text strong>Resumo:</Text>
            <Paragraph style={{ marginTop: 8, whiteSpace: 'pre-wrap' }}>
              {vulnerability.resume}
            </Paragraph>
          </div>

          {/* Sugestões para correção */}
          <div style={{ marginBottom: 16 }}>
            <Text strong>Sugestões para Correção:</Text>
            <Paragraph style={{ marginTop: 8, whiteSpace: 'pre-wrap' }}>
              {vulnerability.suggestionsForCorrection}
            </Paragraph>
          </div>

          {/* Melhores práticas */}
          <div style={{ marginBottom: 16 }}>
            <Text strong>Melhores Práticas:</Text>
            <Paragraph style={{ marginTop: 8, whiteSpace: 'pre-wrap' }}>
              {vulnerability.bestPractices}
            </Paragraph>
          </div>

          <Divider />

          {/* Informações técnicas */}
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Text strong>CWE:</Text>
              <br />
              <Space>
                <Text code>{vulnerability.cwe}</Text>
                <Tooltip title="Copiar CWE">
                  <Button 
                    type="text" 
                    size="small"
                    icon={<CopyOutlined />}
                    onClick={() => copyToClipboard(vulnerability.cwe, 'CWE')}
                  />
                </Tooltip>
              </Space>
            </Col>
            <Col span={12}>
              <Text strong>Tags:</Text>
              <br />
              <Text>{vulnerability.tags}</Text>
            </Col>
            <Col span={24}>
              <Text strong>Métricas CVSS:</Text>
              <br />
              <div style={{ marginTop: 8 }}>
                {renderCVSSMetrics(vulnerability.cvssMetrics)}
              </div>
            </Col>
          </Row>

          <Divider />

          {/* Referências */}
          <div>
            <Text strong>Referências:</Text>
            <div style={{ marginTop: 8 }}>
              {renderReferences(vulnerability.reference)}
            </div>
          </div>

          {/* Informações de data */}
          <div style={{ marginTop: 16, padding: 12, background: '#fafafa', borderRadius: 6 }}>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              Detectado em: {vulnerability.dataFormatada} ({vulnerability.tempoDecorrido})
            </Text>
          </div>
        </div>
      ) : null}
    </Modal>
  );
};

export default CSAVulnerabilityModal;